import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Statistic, Table, Tag, Progress, Alert, Descriptions, Badge } from 'antd';
import {
  ShoppingOutlined,
  ShoppingCartOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  DatabaseOutlined,
  CheckCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { dashboardAPI, orderAPI, productAPI, systemAPI } from '../api';

const Dashboard = () => {
  const [stats, setStats] = useState({
    total_products: 0,
    total_orders: 0,
    total_revenue: 0,
    pending_orders: 0,
  });
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [systemHealth, setSystemHealth] = useState(null);
  const [dbPoolMetrics, setDbPoolMetrics] = useState(null);
  const [healthLoading, setHealthLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
    fetchSystemHealth();
    
    // 每30秒刷新一次系统健康状态
    const healthInterval = setInterval(() => {
      fetchSystemHealth();
    }, 30000);
    
    return () => clearInterval(healthInterval);
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [statsResponse, ordersResponse] = await Promise.all([
        dashboardAPI.getStats(),
        orderAPI.getAll(),
      ]);
      
      setStats(statsResponse.data);
      const ordersPayload = ordersResponse.data?.data || [];
      setRecentOrders(ordersPayload.slice(0, 10)); // Last 10 orders
    } catch (error) {
      
    } finally {
      setLoading(false);
    }
  };

  const fetchSystemHealth = async () => {
    try {
      setHealthLoading(true);
      const [healthResponse, metricsResponse] = await Promise.all([
        systemAPI.getHealth(),
        systemAPI.getDbPoolMetrics(),
      ]);
      
      setSystemHealth(healthResponse.data);
      setDbPoolMetrics(metricsResponse.data);
    } catch (error) {
      
    } finally {
      setHealthLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'orange',
      processing: 'blue',
      shipped: 'green',
      delivered: 'cyan',
      cancelled: 'red',
    };
    return colors[status] || 'default';
  };

  const getStatusText = (status) => {
    const texts = {
      pending: '待处理',
      processing: '处理中',
      shipped: '已发货',
      delivered: '已送达',
      cancelled: '已取消',
    };
    return texts[status] || status;
  };

  const orderColumns = [
    {
      title: '订单号',
      dataIndex: 'order_number',
      key: 'order_number',
    },
    {
      title: '客户',
      dataIndex: 'customer_name',
      key: 'customer_name',
    },
    {
      title: '金额',
      dataIndex: 'total_amount',
      key: 'total_amount',
      render: (amount) => {
        const numericAmount = Number.parseFloat(amount ?? 0) || 0;
        return `${numericAmount.toFixed(2)}`;
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => new Date(date).toLocaleDateString('zh-CN'),
    },
  ];

  return (
    <div>
      <h1 style={{ marginBottom: 24, fontSize: 24, fontWeight: 'bold' }}>概览</h1>

      {/* Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="商品总数"
              value={stats.total_products}
              prefix={<ShoppingOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="订单总数"
              value={stats.total_orders}
              prefix={<ShoppingCartOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="总收入"
              value={stats.total_revenue}
              prefix="$"
              precision={2}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="待处理订单"
              value={stats.pending_orders}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Charts Area */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={12}>
          <Card title="订单状态分布" loading={loading}>
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <Progress
                type="circle"
                percent={stats.total_orders > 0 ? Math.round((stats.pending_orders / stats.total_orders) * 100) : 0}
                format={() => `${stats.pending_orders}/${stats.total_orders}`}
                strokeColor="#fa8c16"
              />
              <div style={{ marginTop: 16, color: '#666' }}>
                待处理订单比例
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="销售概览" loading={loading}>
            <Row gutter={16}>
              <Col span={12}>
                <Statistic
                  title="平均订单金额"
                  value={stats.total_orders > 0 ? (stats.total_revenue / stats.total_orders) : 0}
                  precision={2}
                  prefix="$"
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="活跃商品"
                  value={stats.total_products}
                  suffix="件"
                />
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {/* Recent Orders */}
      <Card title="最近订单" loading={loading}>
        <Table
          columns={orderColumns}
          dataSource={recentOrders}
          rowKey="order_number"
          pagination={{ pageSize: 5 }}
          scroll={{ x: 800 }}
        />
      </Card>
      
      {/* System Health Status */}
      {systemHealth && (
        <Card 
          title={
            <span>
              <DatabaseOutlined style={{ marginRight: 8 }} />
              系统健康状态
            </span>
          }
          loading={healthLoading}
          style={{ marginBottom: 24 }}
          extra={
            <Badge 
              status={systemHealth.status === 'healthy' ? 'success' : 'warning'} 
              text={systemHealth.status === 'healthy' ? '健康' : '警告'}
            />
          }
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <Card 
                type="inner" 
                title="数据库连接池状态"
                size="small"
              >
                {systemHealth.database?.warnings?.length > 0 && (
                  <Alert
                    message="连接池警告"
                    description={
                      <ul style={{ margin: 0, paddingLeft: 20 }}>
                        {systemHealth.database.warnings.map((warning, idx) => (
                          <li key={idx}>{warning}</li>
                        ))}
                      </ul>
                    }
                    type="warning"
                    icon={<WarningOutlined />}
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                )}
                <Descriptions column={2} size="small">
                  <Descriptions.Item label="连接池大小">
                    {dbPoolMetrics?.size || 0}
                  </Descriptions.Item>
                  <Descriptions.Item label="可用连接">
                    <Tag color="green">{dbPoolMetrics?.checked_in || 0}</Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="使用中连接">
                    <Tag color="blue">{dbPoolMetrics?.checked_out || 0}</Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="溢出连接">
                    <Tag color={dbPoolMetrics?.overflow > 0 ? 'orange' : 'default'}>
                      {dbPoolMetrics?.overflow || 0}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="超时设置">
                    {dbPoolMetrics?.timeout || 'N/A'}秒
                  </Descriptions.Item>
                  <Descriptions.Item label="使用率">
                    {dbPoolMetrics ? (
                      <span>
                        {((dbPoolMetrics.checked_out / (dbPoolMetrics.size + dbPoolMetrics.overflow || 1)) * 100).toFixed(1)}%
                      </span>
                    ) : 'N/A'}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card 
                type="inner" 
                title="连接池使用情况"
                size="small"
              >
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                  <Progress
                    type="dashboard"
                    percent={dbPoolMetrics ? 
                      Math.round((dbPoolMetrics.checked_out / (dbPoolMetrics.size + dbPoolMetrics.overflow || 1)) * 100)
                      : 0
                    }
                    format={(percent) => `${percent}%`}
                    strokeColor={{
                      '0%': '#52c41a',
                      '50%': '#faad14',
                      '80%': '#ff4d4f'
                    }}
                  />
                  <div style={{ marginTop: 16, fontSize: 14, color: '#666' }}>
                    {dbPoolMetrics?.checked_out || 0} / {(dbPoolMetrics?.size || 0) + (dbPoolMetrics?.overflow || 0)} 连接使用中
                  </div>
                </div>
              </Card>
            </Col>
          </Row>
        </Card>
      )}
      
    </div>
  );
};

export default Dashboard;
